import type { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { catchError, throwError } from 'rxjs';

import { AlertService } from '@app/core/service';
import { ApiErrorCode } from '@app/data/enums';
import { IApiErrorBody } from '@app/data/schema';
import {
  AuthState,
  Logout,
  PhotoEstimationLinkState,
  ResetPhotoEstimationLink,
} from '@app/data/state';

function extractErrorCode(error: HttpErrorResponse): string | null {
  const body = error.error as IApiErrorBody | null;
  return body?.data?.errorCode ?? null;
}

function extractErrorMessage(error: HttpErrorResponse): string | null {
  const body = error.error as IApiErrorBody | null;
  return body?.message ?? null;
}

function handleErrorToast(
  error: HttpErrorResponse,
  alertService: AlertService,
  store: Store,
  router: Router
): void {
  const errorCode = extractErrorCode(error);
  const errorMessage = extractErrorMessage(error);

  switch (error.status) {
    case 400:
      alertService.errorToast(errorMessage ?? 'The request could not be processed.');
      return;

    case 401: {
      alertService.unauthorizedToast('Your session has expired. Please sign in again.');

      // The session expires in minutes; the link lasts days. Keeping the validated link sends the
      // customer back to the code screen instead of back to the text message.
      const linkIsUsable = store.selectSnapshot(PhotoEstimationLinkState.isUsable);
      store.dispatch(linkIsUsable ? new Logout() : [new Logout(), new ResetPhotoEstimationLink()]);
      void router.navigateByUrl(linkIsUsable ? '/login' : '/');
      return;
    }

    case 422:
      alertService.errorToast(errorMessage ?? 'This could not be processed as requested.');
      return;

    case 429: {
      const retryAfterSeconds = Number(error.headers.get('Retry-After'));
      const waitMessage =
        Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0
          ? `Please try again in ${retryAfterSeconds} seconds.`
          : 'Please try again shortly.';
      alertService.warningToast(errorMessage ?? `Too many attempts. ${waitMessage}`);
      return;
    }

    case 502:
      if (errorCode === ApiErrorCode.OTP_SEND_FAILED) {
        alertService.errorToast(errorMessage ?? 'We could not send the OTP. Please try again.');
        return;
      }
      alertService.errorToast(errorMessage ?? 'The server is temporarily unavailable. Please try again.');
      return;

    default:
      alertService.errorToast(errorMessage ?? 'Something went wrong. Please try again.');
  }
}

export const baseHttpInterceptor: HttpInterceptorFn = (request, next) => {
  const store = inject(Store);
  const alertService = inject(AlertService);
  const router = inject(Router);
  const token = store.selectSnapshot(AuthState.token);

  if (token) {
    request = request.clone({
      setHeaders: { Authorization: `Bearer ${token}` },
    });
  }

  return next(request).pipe(
    catchError((error: HttpErrorResponse) => {
      handleErrorToast(error, alertService, store, router);
      return throwError(() => error);
    })
  );
};
